/**
 * Chat Widget component using Thesys C1 for Generative UI.
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import { Box, Paper } from "@mui/material";
import { useChat } from "./useChat";
import type { ChatWidgetProps, ChatMessage } from "./types";
import type { C1Action } from "./types/c1Types";
import { buildMessageFromC1Action } from "./handlers/c1ActionHandler";
import {
  TOOL_URL_PREFIX,
  TOOL_URL_REGEX,
} from "./constants/chatConstants";
import { ChatHeader } from "./components/ChatHeader";
import { ChatButton } from "./components/ChatButton";
import { ChatEmptyState } from "./components/ChatEmptyState";
import { ChatInput } from "./components/ChatInput";
import { ChatMessage as ChatMessageComponent } from "./components/ChatMessage";
import { ChatLoadingIndicator } from "./components/ChatLoadingIndicator";
import { ChatErrorMessage } from "./components/ChatErrorMessage";


export const ChatWidget: React.FC<ChatWidgetProps> = ({
  planResultRootId,
  planResultTypeId,
  onClose,
}) => {
  const [inputValue, setInputValue] = useState("");
  // Check if we're in a popup window or on the chat page route - if so, always show chat open
  const isPopupWindow = window.opener !== null;
  const isChatPage = window.location.pathname.includes("/translucence-chat/") || 
                     window.location.pathname.includes("/ai-assistant");
  // Determine if we should use full window sizing (popup or dedicated chat page)
  const useFullWindow = isPopupWindow || isChatPage;
  // Always open if we're on the chat page (popup or direct navigation)
  const [isOpen, setIsOpen] = useState(useFullWindow);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const renderedFeedbackRequestIdsRef = useRef<Set<string>>(new Set());

  const {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
    handleContinueConversation,
    progressUpdate,
    isProgressConnected,
    currentStep,
    lastThinkItem,
  } = useChat(planResultRootId, planResultTypeId);

  // Handle C1 component actions (form submissions, button clicks, chart interactions, etc.)
  const handleC1Action = useCallback(
    (action: C1Action) => {
      // Check if this is a continue_conversation action
      // Action has llmFriendlyMessage if it's a conversation continuation
      const hasConversationAction = 
        action.params?.llmFriendlyMessage || 
        action.llmFriendlyMessage;
      
      if (hasConversationAction) {
        // Use action endpoint to maintain conversation context
        handleContinueConversation(action);
      } else if (action.type === "open_url" && action.params?.url) {
        // Handle URL opening (if needed)
        window.open(action.params.url as string, "_blank");
      } else {
        // Fallback: Log unsupported action type
        console.warn("[ChatWidget] Unsupported action type:", action);
      }
    },
    [handleContinueConversation]
  );

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Debug: Log messages whenever they change
  useEffect(() => {
    console.log(`[ChatWidget] 📋 Messages updated: ${messages.length} message(s)`);
    messages.forEach((msg, idx) => {
      console.log(`[ChatWidget]   [${idx}] ${msg.role}: "${msg.content?.substring(0, 50)}..." (id: ${msg.id}, components: ${msg.c1Components?.length || 0})`);
    });
  }, [messages]);

  // Clear feedback request IDs when messages are cleared
  useEffect(() => {
    if (messages.length === 0) {
      renderedFeedbackRequestIdsRef.current.clear();
    }
  }, [messages.length]);

  // Focus input when widget opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Use ref to store latest handleC1Action to avoid dependency issues
  const handleC1ActionRef = useRef(handleC1Action);
  useEffect(() => {
    handleC1ActionRef.current = handleC1Action;
  }, [handleC1Action]);

  // Intercept window.open calls with tool:// URLs and route through handleC1Action
  // Only run once on mount to avoid interfering with SDK initialization
  useEffect(() => {
    
    // Save the original window.open
    const originalWindowOpen = window.open.bind(window);

    // Override window.open
    window.open = (
      url?: string | URL,
      target?: string,
      features?: string
    ): Window | null => {
      // Convert URL to string for checking
      const urlString = url?.toString() || "";
      
      // Log ALL window.open calls for debugging
      
      // Check if this is a tool:// URL
      if (urlString.startsWith(TOOL_URL_PREFIX)) {

        // Extract tool name from URL
        // Format: tool://functions.get_plan_assets -> get_plan_assets
        const toolMatch = urlString.match(TOOL_URL_REGEX);
        if (toolMatch && toolMatch[1]) {
          const toolName = toolMatch[1];

          // Construct an action object that handleC1Action can process
          // This will send a message to trigger the tool call
          const action = {
            params: {
              llmFriendlyMessage: `Call the ${toolName} tool to retrieve the requested data.`,
            },
          };

          
          // Use ref to get latest handleC1Action without causing re-renders
          handleC1ActionRef.current(action);

          // Return null to prevent the blank window from opening
          return null;
        } else {
          console.warn("[ChatWidget] ⚠ Could not extract tool name from URL:", urlString);
          // Still return null to prevent blank window even if we can't parse it
          return null;
        }
      }

      // For non-tool:// URLs, use the original window.open
      return originalWindowOpen(url, target, features);
    };


    // Cleanup: restore original window.open when component unmounts
    return () => {
      window.open = originalWindowOpen;
    };
  }, []); // Empty dependency array - only run once on mount

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) {
      return;
    }

    const message = inputValue.trim();
    setInputValue("");
    await sendMessage(message);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  // Floating chat button (when closed)
  if (!isOpen) {
    return <ChatButton onClick={handleToggle} />;
  }

  // Chat widget (when open)
  return (
    <Paper
      elevation={8}
      sx={{
        position: useFullWindow ? "relative" : "fixed",
        bottom: useFullWindow ? 0 : 24,
        right: useFullWindow ? 0 : 24,
        width: useFullWindow ? "100%" : 400,
        maxWidth: useFullWindow ? "100%" : "calc(100vw - 48px)",
        height: useFullWindow ? "100vh" : 600,
        maxHeight: useFullWindow ? "100vh" : "calc(100vh - 48px)",
        display: "flex",
        flexDirection: "column",
        zIndex: 1000,
        borderRadius: useFullWindow ? 0 : 2,
        overflow: "hidden",
        m: useFullWindow ? 0 : undefined,
      }}
    >
      {/* Header */}
      <ChatHeader
        onClose={() => {
          setIsOpen(false);
          onClose?.();
        }}
      />

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2,
          bgcolor: "grey.50",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {messages.length === 0 && <ChatEmptyState />}

        {messages.map((message: ChatMessage) => {
          const userQuery = messages.find(
            (msg: ChatMessage, idx: number) =>
              idx < messages.indexOf(message) &&
              msg.role === "user"
          )?.content;

          return (
            <ChatMessageComponent
              key={message.id}
              message={message}
              onC1Action={handleC1Action}
              onFeedbackRendered={(requestId) => {
                if (!renderedFeedbackRequestIdsRef.current.has(requestId)) {
                  renderedFeedbackRequestIdsRef.current.add(requestId);
                }
              }}
              renderedFeedbackRequestIds={renderedFeedbackRequestIdsRef.current}
              planResultRootId={planResultRootId}
              planResultTypeId={planResultTypeId}
              userQuery={userQuery}
            />
          );
        })}

        <ChatLoadingIndicator
          isLoading={isLoading}
          progressUpdate={progressUpdate}
          lastThinkItem={lastThinkItem}
          isProgressConnected={isProgressConnected}
        />

        <ChatErrorMessage error={error} />

        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <ChatInput
        value={inputValue}
        onChange={setInputValue}
        onSend={handleSend}
        isLoading={isLoading}
        inputRef={inputRef}
      />
    </Paper>
  );
};

